import type { ILeadRepository } from '@/domain/repositories/ILeadRepository';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import type { CapturedLead } from '@/domain/entities/FakeLead';
import type { LeadCaptureInput } from '@/domain/entities/LeadCaptureInput';
import { API_BASE_URL } from '@/infrastructure/http/apiConfig';
import { DeviceSessionExpiredError } from '@/infrastructure/http/DeviceSessionExpiredError';
import { parseJsonResponse } from '@/infrastructure/http/parseJsonResponse';

/** Consentimento da versão enviada ainda não foi aprovado para este evento
 * em produção (HTTP 409 — HUB-132, CA-14). O runbook do piloto exige
 * aprovar a versão via `POST /events/:id/consent/approve` antes do uso. */
export class ConsentNotApprovedError extends Error {
  constructor() {
    super(
      'Consentimento deste evento ainda não foi aprovado. Peça ao operador ' +
        'para aprovar a versão 1 do consentimento antes de capturar leads.'
    );
    this.name = 'ConsentNotApprovedError';
  }
}

/** `consent_text_version` não corresponde a nenhuma versão existente para
 * este evento (HTTP 422 — HUB-132, CA-02). */
export class ConsentVersionNotFoundError extends Error {
  constructor() {
    super('Versão de consentimento não encontrada para este evento.');
    this.name = 'ConsentVersionNotFoundError';
  }
}

/** Evento não encontrado (HTTP 404) — pode ter sido excluído após o PIN
 * ter sido validado. */
export class LeadEventNotFoundError extends Error {
  constructor() {
    super('Evento não encontrado. Valide o PIN novamente.');
    this.name = 'LeadEventNotFoundError';
  }
}

function buildLeadsUrl(eventId: string): string {
  return `${API_BASE_URL}/events/${eventId}/leads`;
}

function mapErrorResponse(status: number): Error {
  switch (status) {
    case 404:
      return new LeadEventNotFoundError();
    case 409:
      return new ConsentNotApprovedError();
    case 410:
      // Sessão de dispositivo expirada em uso (HUB-235, CA-03): o evento foi
      // invalidado depois que o PIN foi validado.
      return new DeviceSessionExpiredError();
    case 422:
      return new ConsentVersionNotFoundError();
    default:
      return new Error(`Falha ao capturar lead: HTTP ${status}`);
  }
}

/**
 * Implementação que captura um lead (fictício ou de plugin real) chamando
 * `POST /events/{event_id}/leads` com `Authorization: Bearer <jwtToken>`
 * (HUB-132). `phone`/`cpf`/`custom_fields` passam a ser enviados a partir da
 * HUB-237 (CA-20) — sempre presentes no corpo (`null`/`{}` quando o input
 * não os informa, ex.: captura fake), espelhando o schema já aceito pelo
 * backend (`CaptureLeadRequest`).
 */
export class HttpLeadRepository implements ILeadRepository {
  async captureLead(
    session: DeviceSession,
    input: LeadCaptureInput,
    consentTextVersion: number
  ): Promise<CapturedLead | null> {
    const response = await fetch(buildLeadsUrl(session.eventId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.jwtToken}`,
      },
      body: JSON.stringify({
        device_id: session.deviceId,
        name: input.name,
        email: input.email,
        phone: input.phone ?? null,
        cpf: input.cpf ?? null,
        custom_fields: input.customFields ?? {},
        consent_accepted: input.consentAccepted,
        consent_text_version: consentTextVersion,
      }),
    });

    // Consentimento recusado (HUB-201): nenhum lead foi criado, mas isso não
    // é um erro — o backend apenas registra a tentativa em auditoria.
    if (response.status === 202) {
      return null;
    }

    if (!response.ok) {
      throw mapErrorResponse(response.status);
    }

    const data = await parseJsonResponse<Record<string, unknown>>(
      response,
      'Não foi possível confirmar a captura do lead. Tente novamente.'
    );
    return {
      id: String(data['id'] ?? ''),
      name: String(data['name'] ?? ''),
      email: data['email'] === null || data['email'] === undefined ? null : String(data['email']),
    };
  }
}
